import { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import "../compnay/ManageCompany.css";

const MarketplaceOrderTimeline = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [loading, setLoading] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [events, setEvents] = useState([]);

  const companyOptions = useMemo(
    () =>
      companies
        .map((c) => {
          const id = c.compID ?? c.CompID;
          if (id == null) return null;
          const name = c.compName || c.CompName || c.name || c.Name || "Company";
          return { value: id, label: `${name} (#${id})` };
        })
        .filter(Boolean),
    [companies]
  );

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_COMPANIES, {
        params: { PageSize: 500, PageNumber: 1, SortBy: "CompName", SortOrder: "ASC" },
      });
      if (!response.data?.status) {
        setCompanies([]);
        return;
      }
      const payload = response.data.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.companies)
          ? payload.companies
          : Array.isArray(payload?.Companies)
            ? payload.Companies
            : [];
      setCompanies(list);
    } catch {
      setCompanies([]);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const fetchData = useCallback(async () => {
    const compId = Number(companyFilter?.value || 0);
    if (compId <= 0 || !invoiceNo.trim()) {
      showErrorToast("Select a company and enter the invoice number");
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ORDER_TIMELINE, {
        params: { CompID: compId, InvoiceNo: invoiceNo.trim() },
      });
      if (response.data?.status) setEvents(response.data.data || []);
      else showErrorToast(response.data?.message || "Failed to load timeline");
    } catch {
      showErrorToast("Failed to load order timeline");
    } finally {
      setLoading(false);
    }
  }, [companyFilter?.value, invoiceNo]);

  const btnStyle = useMemo(
    () => ({
      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
      boxShadow: `0 2px 8px ${themeColor}40`,
      border: "none",
      color: "#fff",
      padding: "8px 16px",
      borderRadius: 6,
    }),
    [themeColor]
  );

  return (
    <div className={`manage-company-page ${isDarkMode ? "is-dark" : "is-light"}`}>
      {loading && <Preloader />}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div>
          <h1 className="page-header mb-1">Order Timeline</h1>
          <p className="text-muted mb-0">Full event history for a Go Fatafat order (who / when / status).</p>
        </div>
        <button type="button" style={btnStyle} onClick={fetchData}>
          Load timeline
        </button>
      </div>

      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap gap-3 align-items-end">
          <div style={{ minWidth: 260, flex: "1 1 260px", maxWidth: 360 }}>
            <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
              Company
            </label>
            <Select
              value={companyFilter}
              onChange={setCompanyFilter}
              options={companyOptions}
              styles={dropdownStyles}
              placeholder="Select company"
              isClearable
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div style={{ minWidth: 220 }}>
            <FormInput
              label="Invoice No"
              type="text"
              value={invoiceNo}
              onchange={(e) => setInvoiceNo(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Event</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Actor</th>
                  <th>Key</th>
                  <th>Reason</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      No events yet — select a company and invoice, then load
                    </td>
                  </tr>
                ) : (
                  events.map((ev) => (
                    <tr key={ev.history_ID ?? ev.History_ID}>
                      <td>{ev.eventAt ?? ev.EventAt}</td>
                      <td>{ev.eventType ?? ev.EventType}</td>
                      <td>{ev.oldStatus ?? ev.OldStatus ?? "—"}</td>
                      <td>{ev.newStatus ?? ev.NewStatus ?? "—"}</td>
                      <td>
                        {(ev.actorDisplay ?? ev.ActorDisplay) || (ev.actorType ?? ev.ActorType) || "—"}
                      </td>
                      <td>{ev.actorKey ?? ev.ActorKey ?? "—"}</td>
                      <td>{ev.reasonCode ?? ev.ReasonCode ?? "—"}</td>
                      <td>{ev.note ?? ev.Note ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceOrderTimeline;
